import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useEvent } from "../lib/event";
import { startBrandingTour, tourCompleted } from "../lib/tour";
import { useAttraction } from "../lib/attraction";
import { formatSlotTime, formatTime } from "../lib/timeFormat";
import { validateSlotCapacityVsHardCap } from "../lib/validate";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/DashboardLayout";
import WizardBanner from "../components/WizardBanner";
import CompactCalendar from "../components/CompactCalendar";
import { CardListSkeleton } from "../components/Skeleton";
import { CapacityNote } from "../components/CapacityNote";
import { Plus, CalendarDays, Trash2, X, Pencil, AlertTriangle, Ticket, Users, TrendingUp, TrendingDown, Clock, Flame, RefreshCw, Gauge, Mail } from "lucide-react";

function Sparkline({ data = [], className = "" }) {
  if (!data.length || data.every((v) => v === 0)) return null;
  const w = 96, h = 30, max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * (h - 4) - 2}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className={className} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
}

function StatCard({ icon: Icon, label, value, sub, subClass = "text-[#7a7a7a]", sparkline, testid }) {
  return (
    <div className="st-card p-4 flex items-start gap-3" data-testid={testid}>
      <div className="w-11 h-11 rounded-xl bg-[#141414] border border-[#1e1e1e] flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[#dc2626]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">{label}</div>
        <div className="text-2xl font-bold leading-tight mt-0.5">{value}</div>
        {sub && <div className={`text-xs mt-0.5 ${subClass}`}>{sub}</div>}
      </div>
      {sparkline && <div className="self-end pb-0.5"><Sparkline data={sparkline} /></div>}
    </div>
  );
}

function fmtIntelDate(iso) {
  const dt = new Date(iso + "T12:00:00");
  return dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

// Heatmap fill colour by % sold (dark → red as it fills)
function heatColor(pct) {
  if (pct >= 90) return "#dc2626";
  if (pct >= 70) return "#ea580c";
  if (pct >= 45) return "#d97706";
  if (pct >= 20) return "#4d7c0f";
  if (pct > 0) return "#274016";
  return "#1a1a1a";
}

function IntelItem({ icon: Icon, iconColor, tag, title, meta, onClick, testid }) {
  if (!title) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-3 flex items-start gap-3 hover:border-[#dc2626]/50 hover:bg-[#141414] transition-colors"
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${iconColor}1a`, border: `1px solid ${iconColor}44` }}>
        <Icon size={16} style={{ color: iconColor }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#7a7a7a]">{tag}</div>
        <div className="font-semibold text-sm mt-0.5 truncate">{title}</div>
        <div className="text-xs text-[#8a8a8a] mt-0.5">{meta}</div>
      </div>
    </button>
  );
}

function SalesIntelPanel({ intel, selectedDateId, onPick, onClear }) {
  if (!intel) {
    return (
      <div className="st-card p-4 mt-3" data-testid="sales-intel-loading">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">Sales intelligence</div>
        <div className="text-xs text-[#7a7a7a] mt-2">Crunching the numbers…</div>
      </div>
    );
  }
  const ov = intel.overview || {};
  const snap = selectedDateId ? (intel.dates || {})[selectedDateId] : null;

  // ── Selected date → Night Snapshot ──
  if (snap) {
    const vel = snap.velocity_pct || 0;
    const velUp = vel >= 0;
    const slots = snap.slots || [];
    const maxSlot = Math.max(...slots.map((s) => s.pct || 0), 1);
    return (
      <div className="st-card p-4 mt-3" data-testid="night-snapshot">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#dc2626]">Night snapshot</div>
          <button type="button" onClick={onClear} className="text-[#8a8a8a] hover:text-white transition-colors" title="Back to overview" aria-label="Back to overview" data-testid="snapshot-back">
            <RefreshCw size={13} />
          </button>
        </div>
        <div className="flex items-baseline justify-between gap-2 mt-0.5">
          <div className="font-bold text-lg leading-tight">{fmtIntelDate(snap.date)}</div>
          <div className="text-xs text-[#8a8a8a] shrink-0">{snap.days_until > 0 ? `${snap.days_until} days to go` : snap.days_until === 0 ? "Today" : "Past date"}</div>
        </div>

        {/* Velocity */}
        <div className="rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-3 mt-3 flex items-center justify-between" data-testid="snapshot-velocity">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#7a7a7a]"><Gauge size={12} className="text-[#dc2626]" /> 7-day velocity</div>
            <div className={`text-lg font-bold mt-1 flex items-center gap-1 ${velUp ? "text-[#86efac]" : "text-[#fca5a5]"}`}>
              {velUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}{velUp ? "+" : ""}{vel}%
            </div>
            <div className="text-xs text-[#8a8a8a] mt-0.5">{snap.sold_7d} sold vs {snap.sold_prev_7d} prior week</div>
          </div>
          <Sparkline data={snap.sparkline_14d} />
        </div>

        {/* Slot heatmap */}
        {snap.is_slotted && slots.length > 0 ? (
          <div className="rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] p-3 mt-3" data-testid="snapshot-heatmap">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#7a7a7a] mb-2"><Clock size={12} className="text-[#dc2626]" /> Time-slot pressure</div>
            <div className="flex items-end gap-1 h-20">
              {slots.map((s, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative" title={`${s.time} · ${s.sold}/${s.capacity} (${s.pct}%)`}>
                  <div className="w-full rounded-sm transition-all" style={{ height: `${Math.max(6, (s.pct / maxSlot) * 100)}%`, background: heatColor(s.pct) }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[9px] text-[#6a6a6a] mt-1">
              <span>{slots[0]?.time}</span>
              <span>{slots[slots.length - 1]?.time}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {snap.best_slot && (
                <div className="text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Hottest slot</div>
                  <div className="font-semibold text-[#86efac]">{snap.best_slot.time} · {snap.best_slot.pct}%</div>
                </div>
              )}
              {snap.weakest_slot && (
                <div className="text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Quietest slot</div>
                  <div className="font-semibold text-[#fca5a5]">{snap.weakest_slot.time} · {snap.weakest_slot.pct}%</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-xs text-[#7a7a7a] mt-3">This is an all-day (non-slotted) date — no per-slot breakdown.</div>
        )}
      </div>
    );
  }

  // ── Default → Overview ──
  const anyData = ov.fastest_selling || ov.needs_attention || ov.biggest_mover;
  return (
    <div className="st-card p-4 mt-3" data-testid="sales-intel-panel">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#dc2626]"><TrendingUp size={12} /> Sales intelligence</div>
      {!anyData ? (
        <div className="text-xs text-[#7a7a7a] mt-2" data-testid="sales-intel-empty">No sales activity yet. Insights appear as tickets start selling.</div>
      ) : (
        <div className="space-y-2 mt-3">
          <IntelItem
            icon={Flame} iconColor="#ea580c" tag="Fastest selling"
            title={ov.fastest_selling && fmtIntelDate(ov.fastest_selling.date)}
            meta={ov.fastest_selling && `${ov.fastest_selling.sold_7d} tickets in the last 7 days`}
            onClick={() => ov.fastest_selling && onPick(ov.fastest_selling.date_id)}
            testid="intel-fastest"
          />
          <IntelItem
            icon={TrendingUp} iconColor="#22c55e" tag="Biggest mover"
            title={ov.biggest_mover && fmtIntelDate(ov.biggest_mover.date)}
            meta={ov.biggest_mover && `Velocity up ${ov.biggest_mover.velocity_pct}% week on week`}
            onClick={() => ov.biggest_mover && onPick(ov.biggest_mover.date_id)}
            testid="intel-mover"
          />
          <IntelItem
            icon={AlertTriangle} iconColor="#f59e0b" tag="Needs attention"
            title={ov.needs_attention && fmtIntelDate(ov.needs_attention.date)}
            meta={ov.needs_attention && ov.needs_attention.reason}
            onClick={() => ov.needs_attention && onPick(ov.needs_attention.date_id)}
            testid="intel-attention"
          />
          <div className="text-[11px] text-[#6a6a6a] pt-1">Pick any date to see its night snapshot.</div>
        </div>
      )}
    </div>
  );
}


function OpenDaysStats({ stats }) {
  if (!stats) return null;
  const money = (p) => "£" + Math.round((p || 0) / 100).toLocaleString("en-GB");
  const pct = stats.tickets_sold_pct_change;
  const up = pct >= 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="open-days-stats">
      <StatCard icon={CalendarDays} label="Total open days" value={stats.open_days} sub="configured" testid="stat-open-days" />
      <StatCard
        icon={Ticket}
        label="Tickets sold"
        value={stats.tickets_sold.toLocaleString("en-GB")}
        sub={<span><span className={up ? "text-[#86efac]" : "text-[#fca5a5]"}>{up ? "▲" : "▼"} {Math.abs(pct)}%</span> vs previous 30 days</span>}
        subClass=""
        sparkline={stats.tickets_sold_sparkline}
        testid="stat-tickets-sold"
      />
      <StatCard icon={Users} label="Total capacity"
        value={stats.capacity_unlimited_dates > 0 && stats.total_capacity === 0 ? "No limit" : stats.total_capacity.toLocaleString("en-GB")}
        sub={stats.capacity_unlimited_dates > 0
          ? `${stats.capacity_unlimited_dates} date${stats.capacity_unlimited_dates === 1 ? "" : "s"} have a ticket type with no per-slot limit`
          : "across all dates"}
        testid="stat-capacity" />
      <StatCard
        icon={TrendingUp}
        label="Avg fill / revenue potential"
        value={`${stats.avg_fill_pct}%`}
        sub={<span className="text-[#dc2626] font-medium">{money(stats.potential_revenue_pence)}</span>}
        subClass=""
        testid="stat-avg-fill"
      />
    </div>
  );
}

function Modal({ title, onClose, children, testid, wide = false }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up" data-testid={testid}>
      <div className={`st-card w-full ${wide ? "max-w-3xl" : "max-w-md"} p-6 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white" data-testid="modal-close"><X size={18}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Labeled({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs text-[#8a8a8a]">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-[#7a7a7a] mt-1">{hint}</p>}
    </label>
  );
}

function Toast({ text, kind, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className={`fixed top-5 right-5 z-[80] px-4 py-3 rounded-lg border st-fade-up ${
      kind === "err" ? "bg-[#2a1212] border-[#5b1919] text-[#fca5a5]" : "bg-[#0c1d12] border-[#1b3a25] text-[#86efac]"
    }`} data-testid={`toast-${kind}`}>
      {text}
    </div>
  );
}

function MultiSelectCalendar({ selected, onToggle }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = (first.getDay() + 6) % 7;
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  const move = (delta) => { let m = month + delta, y = year; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setMonth(m); setYear(y); };
  const fmt = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => move(-1)} className="st-btn st-btn-ghost px-3">‹</button>
        <div className="font-semibold">{monthNames[month]} {year}</div>
        <button onClick={() => move(1)} className="st-btn st-btn-ghost px-3">›</button>
      </div>
      <div className="grid grid-cols-7 gap-2 text-center text-[10px] uppercase tracking-wider text-[#7a7a7a] mb-2">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dstr = fmt(d);
          const sel = selected.includes(dstr);
          return (
            <button key={i} onClick={() => onToggle(dstr)} data-testid={`multi-day-${dstr}`}
                    className={`st-day ${sel ? "selected" : ""}`}>
              <div className="num">{d}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function OpenDaysPage() {
  const { currentEvent, currentEventId, loading: eventLoading } = useEvent();
  const { attraction } = useAttraction() || {};
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editDate, setEditDate] = useState(null);
  const [remindDate, setRemindDate] = useState(null);
  const [intel, setIntel] = useState(null);
  const [selectedDateId, setSelectedDateId] = useState(null);
  const rowRefs = useRef({});

  const load = async () => {
    if (!currentEventId) { setData(null); setLoading(false); return; }
    setLoading(true);
    try { const r = await api.get(`/events/${currentEventId}`); setData(r.data); }
    catch (e) { setErr("Failed to load event"); }
    finally { setLoading(false); }
    api.get(`/events/${currentEventId}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    api.get(`/events/${currentEventId}/sales-intelligence`).then((r) => setIntel(r.data)).catch(() => setIntel(null));
  };
  useEffect(() => { setSelectedDateId(null); load(); /* eslint-disable-line */ }, [currentEventId]);

  // Select a date → scroll to its row and show its night snapshot.
  const pickDate = (ed) => {
    const id = typeof ed === "string" ? ed : ed?.id;
    if (!id) return;
    setSelectedDateId(id);
    const node = rowRefs.current[id];
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Post-wizard guided tour trigger (Update 24, Change 3b)
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const tourStarted = useRef(false);
  useEffect(() => {
    const t = searchParams.get("tour");
    if (!t || tourStarted.current) return;
    const stripParam = () => {
      const sp = new URLSearchParams(searchParams); sp.delete("tour"); setSearchParams(sp, { replace: true });
    };
    if (t !== "force" && tourCompleted()) { stripParam(); return; }
    // Wait for the event context to resolve, then for the event's data to load,
    // so the tour stats are accurate.
    if (eventLoading) return;
    if (currentEventId && !data) return;
    tourStarted.current = true;
    const openDays = (data?.dates || []).length;
    const ticketTypes = (data?.ticket_types || []).length;
    stripParam();
    setTimeout(() => startBrandingTour({ openDays, ticketTypes, navigate: nav }), 350);
  }, [searchParams, data, currentEventId, eventLoading]); // eslint-disable-line

  if (!currentEventId) {
    return <DashboardLayout title="Open Days">
      <div className="st-card p-12 text-center" data-testid="no-event-prompt">
        <CalendarDays className="mx-auto text-[#dc2626]" />
        <h3 className="text-xl font-bold mt-4">No event selected yet</h3>
        <p className="text-[#8a8a8a] mt-1 mb-6">Use the event switcher above to create one.</p>
      </div>
    </DashboardLayout>;
  }

  const dates = (data?.dates || []).slice().sort((a, b) => a.date.localeCompare(b.date));
  const hasNothing = data && dates.length === 0 && (data.ticket_types || []).length === 0;

  return (
    <DashboardLayout
      title="Open Days"
      subtitle={currentEvent ? `${currentEvent.name} · ${dates.length} configured` : ""}
      actions={
        <div className="flex gap-2">
          <button className="st-btn st-btn-ghost" onClick={() => setBulkOpen(true)} data-testid="add-multiple-btn"><Plus size={14}/> Add multiple</button>
          <button className="st-btn" onClick={() => setAddOpen(true)} data-testid="add-date-btn"><Plus size={14}/> Add date</button>
        </div>
      }
    >
      <WizardBanner eventId={currentEventId} show={hasNothing} />
      {msg && <Toast text={msg} kind="ok" onDone={() => setMsg("")} />}
      {err && <Toast text={err} kind="err" onDone={() => setErr("")} />}

      {dates.length > 0 && <OpenDaysStats stats={stats} />}

      {dates.length === 0 ? (
        loading ? (
          <CardListSkeleton rows={4} testid="open-days-skeleton" />
        ) : (
        <div className="st-card p-12 text-center" data-testid="open-days-empty">
          <CalendarDays className="mx-auto text-[#dc2626]" />
          <h3 className="text-xl font-bold mt-4">No open days yet</h3>
          <p className="text-[#8a8a8a] mt-1">Add the dates you&apos;ll be open and start selling.</p>
          <button onClick={() => setAddOpen(true)} className="st-btn mt-6 inline-flex" data-testid="empty-add-date">Add your first date</button>
        </div>
        )
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {/* Left — List */}
          <div data-testid="open-days-list" className="space-y-2 lg:col-span-3">
            {dates.map(d => {
              const sold = d.tickets_sold ?? 0;
              const cap = d.effective_capacity ?? null;
              const fillPct = cap > 0 ? Math.min(100, Math.round((sold / cap) * 100)) : 0;
              const dt = new Date(d.date + "T12:00:00");
              const dayNum = dt.getDate();
              const monthUpper = dt.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
              const weekday = dt.toLocaleDateString("en-GB", { weekday: "short" });
              const longDate = `${dayNum} ${dt.toLocaleDateString("en-GB", { month: "short" })} ${dt.getFullYear()}`;
              return (
                <div key={d.id} ref={(el) => { rowRefs.current[d.id] = el; }}
                     data-testid={`open-day-row-${d.id}`}
                     onClick={() => pickDate(d)}
                     className={`st-card p-3 sm:p-4 transition-all cursor-pointer ${selectedDateId === d.id ? "!border-[#7f1d1d]" : "hover:border-[#2a2a2a]"}`}>
                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                    {/* calendar box */}
                    <div className="w-14 h-14 rounded-xl bg-[#0f0f0f] border border-[#1e1e1e] flex flex-col items-center justify-center shrink-0">
                      <div className="text-xl font-bold leading-none">{dayNum}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#dc2626] mt-1">{monthUpper}</div>
                    </div>
                    {/* weekday + full date */}
                    <div className="min-w-[92px]">
                      <div className="font-semibold text-sm">{weekday}</div>
                      <div className="text-xs text-[#8a8a8a] mt-0.5">{longDate}</div>
                    </div>
                    {/* time */}
                    <div className="flex items-center gap-1.5 text-sm text-[#cfcfcf] min-w-[150px]">
                      <Clock size={14} className="text-[#8a8a8a]" />
                      {formatSlotTime(d.open_time, d.close_time, attraction, "openHours")}
                    </div>
                    {/* status + slots chips */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.status === "cancelled" ? <span className="st-chip st-chip-red">Cancelled</span> :
                       d.is_blocked ? <span className="st-chip st-chip-amber">Blocked</span> :
                       <span className="st-chip st-chip-green">Active</span>}
                      {(data.event.is_time_slotted && data.event.slot_interval_minutes) && (
                        <span className="st-chip text-[10px]">{data.event.slot_interval_minutes} min slots</span>
                      )}
                      {(d.capacity_override != null || d.price_override_pence != null) && (
                        <span className="st-chip" style={{ color: "#facc15", borderColor: "#facc1555" }}>Override</span>
                      )}
                    </div>
                    {/* sold / capacity */}
                    <div className="ml-auto text-right min-w-[140px]">
                      <div className="text-[10px] text-[#7a7a7a] uppercase tracking-[0.15em]">Sold / Capacity</div>
                      <div className="text-lg font-bold mt-0.5 whitespace-nowrap">
                        <span className="text-[#dc2626]">{sold}</span>
                        <span className="text-[#7a7a7a]"> / </span>
                        <span>{cap == null ? <span className="text-[#8a8a8a] text-sm">no limit</span> : cap.toLocaleString("en-GB")}</span>
                        {cap != null && <span className="text-xs text-[#8a8a8a] ml-2 font-normal">{fillPct}%</span>}
                      </div>
                      <div className="mt-1.5 h-1.5 bg-[#161616] rounded-full overflow-hidden" data-testid={`fill-bar-${d.id}`}>
                        <div className="h-full bg-gradient-to-r from-[#dc2626] to-[#7f1d1d] transition-all" style={{ width: `${fillPct}%` }} />
                      </div>
                      <CapacityNote detail={d.capacity_detail} testid={`capacity-note-${d.id}`} />
                    </div>
                    <button className="st-btn st-btn-ghost text-xs shrink-0" onClick={(e) => { e.stopPropagation(); setEditDate(d); }} data-testid={`edit-date-${d.id}`}>
                      <Pencil size={12}/> Edit
                    </button>
                    <button className="st-btn st-btn-ghost text-xs shrink-0" onClick={(e) => { e.stopPropagation(); setRemindDate(d); }} data-testid={`send-reminder-date-${d.id}`} title="Send the day-before reminder to this night's bookings" aria-label="Send day-before reminder">
                      <span className="inline-flex items-center h-4"><Mail size={14}/></span>
                    </button>
                    <button className="st-btn st-btn-ghost text-xs shrink-0" onClick={(e) => { e.stopPropagation(); nav(`/dashboard/tickets?date=${d.id}`); }} data-testid={`tickets-date-${d.id}`} title="Manage tickets for this date" aria-label="Manage tickets for this date">
                      <span className="inline-flex items-center h-4"><Ticket size={14}/></span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right — Compact calendar (sticky) + Sales intelligence */}
          <div className="lg:sticky lg:top-6 lg:self-start lg:col-span-1">
            <CompactCalendar
              dates={dates}
              mode="selection"
              selectedDate={(dates.find((x) => x.id === selectedDateId) || {}).date}
              onSelect={pickDate}
              testid="open-days-calendar"
              hideLegend
            />
            <SalesIntelPanel
              intel={intel}
              selectedDateId={selectedDateId}
              onPick={(id) => pickDate(dates.find((x) => x.id === id) || id)}
              onClear={() => setSelectedDateId(null)}
            />
          </div>
        </div>
      )}

      {addOpen && (
        <AddDateModal eventId={currentEventId} event={data?.event}
                      onClose={() => setAddOpen(false)}
                      onSaved={() => { setAddOpen(false); setMsg("Date added"); load(); }} />
      )}
      {bulkOpen && (
        <BulkDateModal eventId={currentEventId} event={data?.event}
                       onClose={() => setBulkOpen(false)}
                       onSaved={(n) => { setBulkOpen(false); setMsg(`Added ${n} dates`); load(); }} />
      )}
      {editDate && (
        <EditDateModal eventId={currentEventId} event={data?.event} ed={editDate}
                       onClose={() => setEditDate(null)}
                       onSaved={() => { setEditDate(null); setMsg("Date saved"); load(); }}
                       onDeleted={() => { setEditDate(null); setMsg("Date deleted"); load(); }} />
      )}
      {remindDate && (
        <SendRemindersModal eventId={currentEventId} ed={remindDate}
                            onClose={() => setRemindDate(null)}
                            onSent={(n) => { setRemindDate(null); setMsg(`Reminder queued for ${n} booking${n === 1 ? "" : "s"}`); }} />
      )}
    </DashboardLayout>
  );
}

function SendRemindersModal({ eventId, ed, onClose, onSent }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const when = new Date(ed.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  useEffect(() => {
    api.get(`/events/${eventId}/dates/${ed.id}/reminder-status`)
      .then((r) => setStatus(r.data))
      .catch(() => setErr("Could not load this night's bookings"));
  }, [eventId, ed.id]);

  const send = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/events/${eventId}/dates/${ed.id}/send-reminders`);
      onSent(r.data.queued);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Send failed"); setBusy(false); }
  };

  return (
    <Modal title="Send day-before reminder" onClose={onClose} testid="send-reminders-modal">
      {!status ? <div className="text-sm text-[#8a8a8a]" data-testid="reminder-status-loading">Checking this night's bookings…</div> : (
        <>
          <div className="st-card bg-[#0e0e0e] p-3 text-sm space-y-1" data-testid="reminder-status">
            <div className="flex justify-between"><span className="text-[#8a8a8a]">Night</span><span>{when}</span></div>
            <div className="flex justify-between"><span className="text-[#8a8a8a]">Bookings</span><span data-testid="reminder-orders">{status.orders}</span></div>
            <div className="flex justify-between"><span className="text-[#8a8a8a]">Guests</span><span>{status.guests}</span></div>
            <div className="flex justify-between"><span className="text-[#8a8a8a]">Already reminded</span><span data-testid="reminder-already-sent">{status.already_sent}</span></div>
          </div>
          {status.already_sent > 0 && (
            <div className="rounded-lg border border-[#3a2a0a] bg-[#1a1205] p-3 text-sm text-[#e8c987] mt-3" data-testid="reminder-duplicate-warn">
              {status.already_sent} of these have had the reminder already — they'll get it again.
            </div>
          )}
          <p className="text-[11px] text-[#7a7a7a] mt-3">
            Uses your current <b>Day-before reminder</b> template. Texts go out too if SMS is switched on.
          </p>
          <button className="st-btn w-full justify-center mt-4" disabled={busy || status.orders === 0} onClick={send} data-testid="send-reminders-confirm">
            {busy ? "Sending…" : status.orders === 0 ? "No bookings on this night" : `Send to all ${status.orders} booking${status.orders === 1 ? "" : "s"}`}
          </button>
        </>
      )}
      {err && <div className="text-sm text-[#fca5a5] mt-3" data-testid="send-reminders-error">{err}</div>}
    </Modal>
  );
}

function AddDateModal({ eventId, event, onClose, onSaved }) {
  const [date, setDate] = useState("");
  const [openT, setOpenT] = useState("19:00");
  const [closeT, setCloseT] = useState("23:00");
  const [slotted, setSlotted] = useState(event?.is_time_slotted ?? false);
  const [interval, setIv] = useState(event?.slot_interval_minutes || 30);
  const [cap, setCap] = useState(30);
  const [hardCap, setHardCap] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  // calculate number of slots given times + interval
  const numSlots = (() => {
    if (!slotted) return 0;
    const [oh, om] = openT.split(":").map(Number);
    const [ch, cm] = closeT.split(":").map(Number);
    const mins = (ch * 60 + cm) - (oh * 60 + om);
    return Math.max(0, Math.floor(mins / Number(interval || 1)));
  })();
  const conflict = validateSlotCapacityVsHardCap({
    capacityPerSlot: cap, hardCap, slotCount: numSlots,
  });
  const hasConflict = conflict.hasConflict;

  const autoDistribute = () => setCap(conflict.suggestedPerSlot);
  const evenPerSlot = conflict.suggestedPerSlot;
  const remainder = conflict.remainder;

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/events/${eventId}/dates`, {
        date, open_time: openT, close_time: closeT, max_capacity: Number(cap),
        hard_cap: hardCap === "" ? null : Number(hardCap),
        is_time_slotted: slotted, slot_interval_minutes: slotted ? Number(interval) : null,
      });
      onSaved();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add date" onClose={onClose} testid="add-date-modal">
      <div className="space-y-3">
        <Labeled label="Date"><input type="date" className="st-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="add-date-input" /></Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Open"><input type="time" className="st-input" value={openT} onChange={(e) => setOpenT(e.target.value)} data-testid="add-open" /></Labeled>
          <Labeled label="Close"><input type="time" className="st-input" value={closeT} onChange={(e) => setCloseT(e.target.value)} data-testid="add-close" /></Labeled>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={slotted} onChange={(e) => setSlotted(e.target.checked)} data-testid="add-slotted" /> Time-slotted
        </label>
        {slotted && (
          <Labeled label="Slot interval (minutes)">
            <select className="st-input" value={interval} onChange={(e) => setIv(e.target.value)} data-testid="add-interval">
              {[15, 20, 30, 45, 60, 90].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Labeled>
        )}
        {slotted ? (
          <>
            <Labeled label="Capacity per time slot"
                     hint="The maximum number of tickets available in each individual time slot.">
              <input type="number" min="1" className="st-input" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="add-capacity" />
            </Labeled>
            <Labeled label="Maximum capacity for this date (optional)"
                     hint="Hard ceiling across all slots, regardless of per-slot caps. Leave blank for no overall limit.">
              <input type="number" min="1" className="st-input" value={hardCap} onChange={(e) => setHardCap(e.target.value)} placeholder="No overall limit" data-testid="add-hardcap" />
            </Labeled>
            {hasConflict && (
              <div className="p-3 rounded-lg bg-[#1a1409] border border-[#facc1544]" data-testid="conflict-warning">
                <div className="flex items-start gap-2 text-sm text-[#facc15]">
                  <AlertTriangle size={14} className="mt-0.5"/>
                  <div>
                    Your per-slot capacity of <strong>{cap}</strong> across <strong>{numSlots}</strong> slots totals <strong>{conflict.total}</strong>, which exceeds your date hard cap of <strong>{conflict.hardCap}</strong>. Auto-distribute?
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="button" className="st-btn text-xs" onClick={autoDistribute} data-testid="auto-distribute">
                    Yes, auto-distribute ({evenPerSlot} per slot{remainder > 0 ? ` · ${remainder} under cap` : ""})
                  </button>
                  <button type="button" className="st-btn st-btn-ghost text-xs">No, keep my values</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <Labeled label="Max capacity for this date">
            <input type="number" min="1" className="st-input" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="add-capacity" />
          </Labeled>
        )}
        {err && <div className="text-sm text-[#fca5a5]">{err}</div>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="st-btn" disabled={busy || !date} onClick={save} data-testid="add-date-save">{busy ? "Saving…" : "Save date"}</button>
      </div>
    </Modal>
  );
}

function BulkDateModal({ eventId, event, onClose, onSaved }) {
  const [picked, setPicked] = useState([]);
  const [openT, setOpenT] = useState("19:00");
  const [closeT, setCloseT] = useState("23:00");
  const [slotted, setSlotted] = useState(event?.is_time_slotted ?? false);
  const [interval, setIv] = useState(event?.slot_interval_minutes || 30);
  const [cap, setCap] = useState(100);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const toggle = (d) => setPicked(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  const save = async () => {
    if (!picked.length) return;
    setErr(""); setBusy(true);
    try {
      const r = await api.post(`/events/${eventId}/dates/bulk`, {
        dates: picked.map(d => ({ date: d, open_time: openT, close_time: closeT, max_capacity: Number(cap) })),
        is_time_slotted: slotted, slot_interval_minutes: slotted ? Number(interval) : null,
      });
      onSaved(r.data.created);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add multiple dates" onClose={onClose} wide testid="bulk-date-modal">
      <div className="grid md:grid-cols-2 gap-6">
        <MultiSelectCalendar selected={picked} onToggle={toggle} />
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-[#7a7a7a]">{picked.length} dates selected</div>
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Open"><input type="time" className="st-input" value={openT} onChange={(e) => setOpenT(e.target.value)} /></Labeled>
            <Labeled label="Close"><input type="time" className="st-input" value={closeT} onChange={(e) => setCloseT(e.target.value)} /></Labeled>
          </div>
          <Labeled label="Max capacity"><input type="number" min="1" className="st-input" value={cap} onChange={(e) => setCap(e.target.value)} /></Labeled>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={slotted} onChange={(e) => setSlotted(e.target.checked)} /> Time-slotted
          </label>
          {slotted && (
            <Labeled label="Slot interval (minutes)">
              <select className="st-input" value={interval} onChange={(e) => setIv(e.target.value)}>
                {[15, 20, 30, 45, 60, 90].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </Labeled>
          )}
          {err && <div className="text-sm text-[#fca5a5]">{err}</div>}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="st-btn" disabled={busy || !picked.length} onClick={save} data-testid="bulk-date-save">{busy ? "Saving…" : `Add ${picked.length} dates`}</button>
      </div>
    </Modal>
  );
}

function EditDateModal({ eventId, event, ed, onClose, onSaved, onDeleted }) {
  const [openT, setOpenT] = useState(ed.open_time);
  const [closeT, setCloseT] = useState(ed.close_time);
  const [cap, setCap] = useState(ed.max_capacity);
  const [hardCap, setHardCap] = useState(ed.hard_cap != null ? String(ed.hard_cap) : "");
  const [blocked, setBlocked] = useState(ed.is_blocked);
  const [interval, setIv] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [salesBlock, setSalesBlock] = useState(null);
  const hasOrders = (ed.tickets_sold || 0) > 0;
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const slotted = (ed.time_slots || []).length > 0;

  // hard-cap × per-slot validator (Fix 6)
  const numSlots = (ed.time_slots || []).length;
  const intervalForCalc = interval || event?.slot_interval_minutes || 30;
  const computedSlotCount = slotted ? (() => {
    const [oh, om] = openT.split(":").map(Number);
    const [ch, cm] = closeT.split(":").map(Number);
    const mins = (ch * 60 + cm) - (oh * 60 + om);
    return Math.max(0, Math.floor(mins / Number(intervalForCalc || 1)));
  })() : numSlots;
  const conflict = validateSlotCapacityVsHardCap({
    capacityPerSlot: cap, hardCap, slotCount: computedSlotCount,
  });

  const callSave = async (confirmRemove = false) => {
    setErr(""); setBusy(true); setPendingRemoval(null); setSalesBlock(null);
    try {
      const patch = {
        open_time: openT, close_time: closeT, max_capacity: Number(cap),
        is_blocked: blocked,
        confirm_remove_slots: confirmRemove,
      };
      if (hardCap === "") patch.reset_hard_cap = true; else patch.hard_cap = Number(hardCap);
      if (interval) patch.slot_interval_minutes = Number(interval);
      await api.patch(`/events/${eventId}/dates/${ed.id}`, patch);
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "confirm_remove_empty_slots") {
          setPendingRemoval(detail.affected_empty_slots || []);
        } else if (detail.code === "reduce_hours_blocked") {
          setSalesBlock(detail.affected_slots_with_sales || []);
          setErr(detail.message);
        } else {
          setErr(detail.message || formatErr(detail));
        }
      } else { setErr(formatErr(detail) || e.message); }
    }
    finally { setBusy(false); }
  };
  const save = () => callSave(false);
  const confirmAndSave = () => callSave(true);

  const deleteDate = async () => {
    const ok = await confirmDialog({
      title: "Delete this date?",
      description: "This will permanently remove this date. This action cannot be undone, and is only available because no tickets have been sold for this date.",
      confirmLabel: "Yes, delete date",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await api.delete(`/events/${eventId}/dates/${ed.id}`); onDeleted(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit ${ed.date}`} onClose={onClose} testid="edit-date-modal">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Open"><input type="time" className="st-input" value={openT} onChange={(e) => setOpenT(e.target.value)} data-testid="edit-open" /></Labeled>
          <Labeled label="Close"><input type="time" className="st-input" value={closeT} onChange={(e) => setCloseT(e.target.value)} data-testid="edit-close" /></Labeled>
        </div>
        {slotted ? (
          <>
            <Labeled label="Default capacity per slot" hint="Newly added slots use this value. Existing per-slot overrides are preserved.">
              <input type="number" className="st-input" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="edit-capacity" />
            </Labeled>
            <Labeled label="Date hard cap (optional)" hint="Ceiling across all slots. Leave blank for no overall limit.">
              <input type="number" className="st-input" value={hardCap} onChange={(e) => setHardCap(e.target.value)} placeholder="No overall limit" data-testid="edit-hardcap" />
            </Labeled>
            {conflict.hasConflict && (
              <div className="p-3 rounded-lg bg-[#1a1409] border border-[#facc1544]" data-testid="edit-conflict-warning">
                <div className="flex items-start gap-2 text-sm text-[#facc15]">
                  <AlertTriangle size={14} className="mt-0.5"/>
                  <div>
                    Your per-slot capacity of <strong>{cap}</strong> across <strong>{conflict.slotCount}</strong> slots totals <strong>{conflict.total}</strong>, which exceeds your date hard cap of <strong>{conflict.hardCap}</strong>. Auto-distribute?
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="button" className="st-btn text-xs" onClick={() => setCap(conflict.suggestedPerSlot)} data-testid="edit-auto-distribute">
                    Yes, auto-distribute ({conflict.suggestedPerSlot} per slot{conflict.remainder > 0 ? ` · ${conflict.remainder} under cap` : ""})
                  </button>
                  <button type="button" className="st-btn st-btn-ghost text-xs">No, keep my values</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <Labeled label="Max capacity">
            <input type="number" className="st-input" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="edit-capacity" />
          </Labeled>
        )}
        <Labeled label="Slot interval (only if no orders)">
          <select className="st-input" value={interval || ""} onChange={(e) => setIv(e.target.value || null)} data-testid="edit-interval">
            <option value="">Keep current</option>
            {[15, 20, 30, 45, 60, 90].map(v => <option key={v} value={v}>{v} min</option>)}
          </select>
          {hasOrders && <p className="text-[11px] text-[#fbbf24] mt-1">Orders exist — changing interval will fail.</p>}
        </Labeled>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} data-testid="edit-blocked" /> Block this date (no new sales)
        </label>

        {pendingRemoval && (
          <div className="p-3 rounded-lg bg-[#1a1409] border border-[#facc1544]" data-testid="confirm-remove-banner">
            <div className="text-sm text-[#facc15] flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5"/>
              <div>Reducing open hours will remove <strong>{pendingRemoval.length}</strong> empty time slot(s):
                <span className="block st-mono text-xs mt-1">{pendingRemoval.map(s => `${s.start_time}-${s.end_time}`).join(", ")}</span>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button type="button" className="st-btn text-xs" onClick={confirmAndSave} disabled={busy} data-testid="confirm-remove-slots">Confirm and remove</button>
              <button type="button" className="st-btn st-btn-ghost text-xs" onClick={() => setPendingRemoval(null)}>Cancel</button>
            </div>
          </div>
        )}

        {salesBlock && (
          <div className="p-3 rounded-lg bg-[#2a1212] border border-[#5b1919]" data-testid="sales-block-banner">
            <div className="text-sm text-[#fca5a5]">Cannot reduce open hours — tickets sold on:
              <ul className="mt-1 text-xs st-mono">
                {salesBlock.map(s => <li key={s.slot_id}>{s.start_time}-{s.end_time}: {s.tickets_sold} tickets</li>)}
              </ul>
            </div>
          </div>
        )}

        {err && !pendingRemoval && !salesBlock && <div className="text-sm text-[#fca5a5]">{err}</div>}
      </div>
      <div className="flex justify-between items-center gap-2 mt-5 pt-4 border-t border-[#1a1a1a]">
        <div>
          {!hasOrders && (
            <button
              className="text-[#7a7a7a] hover:text-[#dc2626] p-2 rounded-md hover:bg-[#161616] transition-colors"
              onClick={deleteDate}
              disabled={busy}
              data-testid="edit-delete-date"
              aria-label="Delete date"
              title="Delete date"
            >
              <Trash2 size={16}/>
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button className="st-btn st-btn-ghost" onClick={onClose}>Close</button>
          <button className="st-btn" onClick={save} disabled={busy || conflict.hasConflict} data-testid="edit-save">Save</button>
        </div>
      </div>
    </Modal>
  );
}
